import { DraftAccount } from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";

// Of the given selected account ids, the subset whose account satisfies
// `predicate`. Builds the matching-id set once from the accounts that pass the
// predicate, then intersects the selection against it (preserving selection
// order). Shared by the bulk-action gates below.
const filterAccountIdsByPredicate = ({
    accounts,
    selectedIds,
    predicate,
}: {
    accounts: DraftAccount[];
    selectedIds: string[];
    predicate: (account: DraftAccount) => boolean;
}): string[] => {
    const matchingIds = new Set(
        accounts.filter(predicate).map((account) => account.id),
    );
    return selectedIds.filter((id) => matchingIds.has(id));
};

// Of the given selected account ids, the subset that may be bulk-deleted: those
// whose host capability leaves Delete unblocked (deleteBlockReason == null).
export const filterDeletableAccountIds = ({
    accounts,
    selectedIds,
    getRowCapabilities,
}: {
    accounts: DraftAccount[];
    selectedIds: string[];
    getRowCapabilities: GetRowCapabilities;
}): string[] =>
    filterAccountIdsByPredicate({
        accounts,
        selectedIds,
        predicate: (account) =>
            getRowCapabilities(account).deleteBlockReason == null,
    });

// Of the given selected account ids, the subset that may be bulk-disabled:
// currently-enabled accounts whose host capability leaves Archive unblocked.
export const filterDisablableAccountIds = ({
    accounts,
    selectedIds,
    getRowCapabilities,
}: {
    accounts: DraftAccount[];
    selectedIds: string[];
    getRowCapabilities: GetRowCapabilities;
}): string[] =>
    filterAccountIdsByPredicate({
        accounts,
        selectedIds,
        predicate: (account) =>
            !account.isDisabled &&
            getRowCapabilities(account).archiveBlockReason == null,
    });

// Of the given selected account ids, the subset that may be bulk-enabled: only
// accounts that are currently disabled. When no selected account is disabled
// there is nothing to do, so bulk Enable is gated off.
export const filterEnablableAccountIds = ({
    accounts,
    selectedIds,
}: {
    accounts: DraftAccount[];
    selectedIds: string[];
}): string[] =>
    filterAccountIdsByPredicate({
        accounts,
        selectedIds,
        predicate: (account) => account.isDisabled,
    });
